import filtersApi from '@/app/features/ams/filters/filters-service';
import { CodeType } from '@/app/shared/enums/code-type.enum';
import { Motif } from '@/app/shared/enums/motif-enum';
import { useAlertStore } from '@/app/shared/stores/alert-store';
import { useAuthStore } from '@/app/shared/stores/auth-store';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { HistoryData } from '@/app/shared/types/history-data.interface';
import { DetailPage } from '@/app/shared/types/models/detail.page.interface';
import { ViewCodeMetaInformation } from '@/app/shared/types/models/view-code-meta-information.interface';
import {
  CheckCodeSyntaxPayload,
  FilterPayload,
} from '@/app/shared/types/params/code-meta-information-params.interface';
import { FilterOrDsLockPayload } from '@/app/shared/types/params/filter-lock-params.interface';
import {
  PaginationParams,
  SearchParams,
} from '@/app/shared/types/params/request-params.interface';
import { TabHeader } from '@/app/shared/types/tab-header.interface';
import { VersionControl } from '@/app/shared/types/version-control.interface';
import { WhereUsedData } from '@/app/shared/types/where-used-data.interface';
import { EntityReferencesDependencies } from '@/app/shared/types/entity-references-dependencies.interface';
import { defineStore } from 'pinia';
import { Filters } from '@/app/shared/types/filters.interface';
import { Ref, ref } from 'vue';

export const useFiltersStore = defineStore('filtersStore', () => {
  const authStore = useAuthStore();
  const alertStore = useAlertStore();

  const filters: Ref<PaginatedResponse<Filters>> = ref(
    {} as PaginatedResponse<Filters>
  );
  const historyData: Ref<PaginatedResponse<HistoryData>> = ref(
    {} as PaginatedResponse<HistoryData>
  );
  const whereUsedData: Ref<Array<WhereUsedData>> = ref([]);
  const filterDetailsMap = ref(new Map());
  const newFilter: Ref<DetailPage> = ref({} as DetailPage);
  //TODO: Move this to a separate interface
  const filterValidate: Ref<{
    WARNING: string;
    FILTER: Filters;
    ARFS: any;
  }> = ref({} as any);
  //TODO: Create an interface for this
  const filterArgument = ref({} as any);
  const massDeleteDependencies: Ref<Array<EntityReferencesDependencies>> = ref(
    []
  );
  const deleteEntityIds: Ref<Array<number>> = ref([]);
  const tabs: Ref<Array<TabHeader>> = ref([{ id: 0, name: 'All Filters' }]);
  const selectedTab = ref(0);
  const requestParams: Ref<SearchParams> = ref({
    codeType: CodeType.FILTER,
  });

  const fetchFilters = async (clientId: number, params: PaginationParams) => {
    const {
      data: { data },
    } = await filtersApi.getFilters(clientId, params);
    filters.value = data;
  };

  const fetchHistoryDataById = async (
    clientId: number,
    filterId: number,
    queryParams: PaginationParams
  ) => {
    const {
      data: { data },
    } = await filtersApi.getHistoryByFilterId(clientId, filterId, queryParams);

    historyData.value = data || {};
  };

  const updateFilter = async (
    clientId: number,
    filterId: number,
    filter: FilterPayload
  ) => {
    const {
      data: { data },
    } = await filtersApi.updateFilter(clientId, filterId, filter);
    const index = tabs.value.findIndex((tab) => tab.id === filterId);
    tabs.value[index].name = filter.shortName;
    newFilter.value.overviewData = data;
    filterDetailsMap.value.get(filterId).overviewData =
      newFilter.value.overviewData;
  };

  const createFilter = async (clientId: number, filter: FilterPayload) => {
    const {
      data: { data },
    } = await filtersApi.createFilter(clientId, filter);
    newFilter.value = data;
  };

  const lockFilterOrDs = async (
    filterOrDsLockPayload: FilterOrDsLockPayload
  ) => {
    const { clientId, filterId, isAutoLock, dataSourceName } =
      filterOrDsLockPayload;

    const { data } = await filtersApi.lockFilterOrDs(filterOrDsLockPayload);
    if (isAutoLock) {
      updateAutoLockInfoInOverviewList({
        clientId,
        filterId,
        autoLockedByUser: authStore.username,
        autoLockExpiry: data?.data?.expiryEndTime,
        dataSourceName,
      });
    }
  };

  const unlockFilterOrDs = async (
    filterOrDsLockPayload: FilterOrDsLockPayload
  ) => {
    const { clientId, filterId, isAutoLock, dataSourceName } =
      filterOrDsLockPayload;

    await filtersApi.unlockFilterOrDs(filterOrDsLockPayload);
    if (isAutoLock) {
      updateAutoLockInfoInOverviewList({
        clientId,
        filterId,
        autoLockedByUser: null,
        autoLockExpiry: null,
        dataSourceName,
      });
    }
  };

  const removeTab = (selectedId: number) => {
    const index = tabs.value.findIndex((tab) => tab.id === selectedId);
    if (index) tabs.value.splice(index, 1);
  };

  const updateRequestParams = (params: SearchParams) => {
    Object.keys(params).forEach((key) => {
      requestParams.value[key as keyof SearchParams] =
        params[key as keyof SearchParams];
    });
  };

  const fetchFilterById = async (clientId: number, filterId: number) => {
    const {
      data: { data },
    } = await filtersApi.getFilterById(clientId, filterId);
    newFilter.value = data;
  };

  const createFilterTab = async (clientId: number, detailPage: DetailPage) => {
    const { id, name } = detailPage;
    if (!detailPage.isNew) {
      const {
        data: { data },
      } = await filtersApi.getFilterById(clientId, id);
      detailPage.overviewData = data;
      filterDetailsMap.value.set(id, detailPage);
    }
    //if tab is already opened, switch to that tab
    const tabIndex = tabs.value.findIndex(
      (tab: TabHeader) => tab.name === name
    );
    if (tabIndex === -1) {
      tabs.value.push({
        id: id,
        name: name,
        autoLocked: false,
      });
      selectedTab.value = tabs.value.length - 1;
    } else {
      selectedTab.value = tabIndex;
    }
  };

  const createNewfilterTab = async (
    clientId: number,
    detailPage: DetailPage,
    // TODO: Create an interface for this attribute or use an existing one
    defaultData: any
  ) => {
    if (detailPage.isNew) {
      detailPage.overviewData = defaultData;
      filterDetailsMap.value.set(detailPage.id, detailPage);
    } else {
      const resp = await filtersApi.getFilterById(clientId, detailPage.id);
      detailPage.overviewData = resp.data.data;
      filterDetailsMap.value.set(detailPage.id, detailPage);
    }
    //if tab is already opened, switch to that tab

    tabs.value.push({
      id: detailPage.id,
      name: detailPage.name,
      autoLocked: true,
    });
  };
  // TODO: Create an interface for this attribute or use an existing one
  const setOverviewDataById = (filterId: string | number, value: any) => {
    const data = filterDetailsMap.value.get(filterId);
    data.overviewData = value;
  };

  const updateAutoLockStatusForTab = (
    filterId: number,
    autoLockValue: boolean
  ) => {
    const fIndex = tabs.value.findIndex((elem) => elem.id === filterId);
    if (fIndex !== -1) {
      tabs.value[fIndex].autoLocked = autoLockValue;
    }
  };

  //TODO: Move type definition to an interface and desctructure the object in the
  // receiving parameters
  const updateAutoLockInfoInOverviewList = (autoLockInfo: {
    clientId: number;
    filterId: number;
    autoLockedByUser: string | null;
    autoLockExpiry: number | null;
    dataSourceName?: string;
  }) => {
    const {
      clientId,
      filterId,
      autoLockedByUser,
      autoLockExpiry,
      dataSourceName,
    } = autoLockInfo;
    const fIndex = filters.value.content.findIndex(
      (elem: ViewCodeMetaInformation) =>
        elem.id === filterId && elem.clientId === clientId
    );
    if (fIndex !== -1) {
      const data = dataSourceName
        ? filters.value?.content?.[fIndex]?.versionControls?.find(
            (vc: VersionControl) => vc?.dataSource?.name === dataSourceName
          )
        : filters.value?.content?.[fIndex]?.versionControls?.find(
            (vc: VersionControl) => vc?.dataSource == null
          );

      if (data) {
        data.autoLockedByUserId = autoLockedByUser;
        data.autoLockExpiry = autoLockExpiry;
        data.lockedByUsername = autoLockedByUser;
      }

      const notSelectedDs =
        dataSourceName &&
        filters.value?.content?.[fIndex]?.versionControls.filter(
          (vc: VersionControl) => vc.dataSource?.name != dataSourceName
        );

      if (Array.isArray(notSelectedDs)) {
        notSelectedDs?.forEach((vc: VersionControl) => {
          vc.autoLockExpiry = null;
          vc.autoLockedByUserId = null;
          vc.lockedByUsername = null;
        });
      }
    }
  };

  const saveCreatedFilter = (oldId: number, data: Filters) => {
    const { id, shortName } = data;
    filterDetailsMap.value.delete(oldId);
    filterDetailsMap.value.set(data.id, {
      id,
      name: shortName,
      isNew: false,
      dirtyTab: new Map<string, boolean>(),
      isCopied: {
        id: -1,
        isCopy: false,
      },
      overviewData: data,
    });
    const fIndex = tabs.value.findIndex((elem) => elem.id === oldId);
    tabs.value[fIndex].id = fIndex > 0 ? id : -1;
    tabs.value[fIndex].name = fIndex > 0 ? shortName : '';
  };

  const setTabNewFieldById = (filterId: string | number, value: boolean) => {
    const data = filterDetailsMap.value.get(filterId);
    data.isNew = value;
  };

  const updateCodeInVersionControlsById = (filterId: number, code: string) => {
    const versionControls =
      filterDetailsMap.value.get(filterId).overviewData.versionControls;
    versionControls[0].code = code;
  };

  const setTabDirtyFieldById = (
    filterId: string | number,
    tabName: string,
    isDirty: boolean
  ) => {
    filterDetailsMap.value.get(filterId)?.dirtyTab?.set(tabName, isDirty);
  };

  const getTabDirtyStatus = (filterId: string | number, tabName: string) => {
    return filterDetailsMap.value.get(filterId)?.dirtyTab?.get(tabName);
  };

  const setTabIsCopyStatusToDefaultById = (filterId: string | number) => {
    const data = filterDetailsMap.value.get(filterId);
    data.isCopied.id = -1;
    data.isCopied.isCopy = false;
  };

  const copyFilter = async (
    filter: FilterPayload,
    clientId: number,
    filterId: string | number
  ) => {
    const resp = await filtersApi.copyFilter(filter, clientId, filterId);
    newFilter.value = resp?.data?.data;
  };

  const checkCodeSyntax = async (
    clientId: number,
    code: CheckCodeSyntaxPayload
  ) => {
    await filtersApi.checkCodeSyntax(clientId, code);
  };

  const saveFilterCode = async (
    clientId: number,
    filterId: number | string,
    codeEntity: CheckCodeSyntaxPayload
  ) => {
    await filtersApi.saveFilterCode(clientId, filterId, codeEntity);
  };

  const argumentsFilter = async (
    clientId: number,
    filterId: number,
    datasourceName: string
  ) => {
    const resp = await filtersApi.getFilterArguments(
      clientId,
      filterId,
      datasourceName
    );
    filterArgument.value = resp.data.data;
  };

  const validateFilterCode = async (
    clientId: number,
    filterId: number,
    dataSourceName: string,
    file: FormData,
    argumentsInput: Array<string>
  ) => {
    const resp = await filtersApi.validateFilterCode(
      clientId,
      filterId,
      dataSourceName,
      file,
      argumentsInput
    );
    filterValidate.value = resp.data.data;
  };

  const deleteFilterTab = (filterId: number) => {
    filterDetailsMap.value.delete(filterId);
    removeTab(filterId);
  };

  const fetchWhereUsedDetails = async (clientId: number, filterId: number) => {
    try {
      const resp = await filtersApi.getWhereUsedByFilterId(clientId, filterId);
      whereUsedData.value = resp.data.data || [];
    } catch (e) {
      const error: any = e;
      alertStore.showNotification(
        error.errors[0].message,
        'Filters',
        Motif.ERROR
      );
    }
  };

  const rollbackFilterCode = async (
    clientId: number,
    rollbackRevisionId: number
  ) => {
    await filtersApi.rollbackFilterCode(clientId, rollbackRevisionId);
  };

  const checkDuplicateShortName = async (clientId: number, name: string) => {
    await filtersApi.checkDuplicateShortName(clientId, name);
  };

  const lockAllFilters = async (clientId: number) => {
    await filtersApi.lockAllFilters(clientId);
  };

  const unlockAllFilters = async (clientId: number) => {
    await filtersApi.unlockAllFilters(clientId);
  };

  const lockAllDataSources = async (
    clientId: number,
    dataSourceName: string
  ) => {
    await filtersApi.lockAllDataSources(clientId, dataSourceName);
  };

  const unlockAllDataSources = async (
    clientId: number,
    dataSourceName: string
  ) => {
    await filtersApi.unlockAllDataSources(clientId, dataSourceName);
  };

  const deleteFilters = async (
    clientId: number,
    selectedFilters?: Array<number>
  ) => {
    await filtersApi.deleteFilters(clientId, selectedFilters);
  };

  const retrieveCyclicDependencies = async (
    clientId: number,
    selectedFilters?: Array<number>
  ) => {
    const {
      data: {
        data: { entities, ids },
      },
    } = await filtersApi.retrieveCyclicDependencies(clientId, selectedFilters);
    massDeleteDependencies.value = entities;
    deleteEntityIds.value = ids;
  };

  return {
    filters,
    historyData,
    whereUsedData,
    filterDetailsMap,
    newFilter,
    filterValidate,
    filterArgument,
    massDeleteDependencies,
    deleteEntityIds,
    tabs,
    selectedTab,
    requestParams,
    fetchFilters,
    fetchHistoryDataById,
    updateFilter,
    createFilter,
    lockFilterOrDs,
    unlockFilterOrDs,
    removeTab,
    updateRequestParams,
    fetchFilterById,
    createFilterTab,
    createNewfilterTab,
    setOverviewDataById,
    updateAutoLockStatusForTab,
    updateAutoLockInfoInOverviewList,
    saveCreatedFilter,
    setTabNewFieldById,
    updateCodeInVersionControlsById,
    setTabDirtyFieldById,
    getTabDirtyStatus,
    setTabIsCopyStatusToDefaultById,
    copyFilter,
    checkCodeSyntax,
    saveFilterCode,
    argumentsFilter,
    validateFilterCode,
    deleteFilterTab,
    fetchWhereUsedDetails,
    rollbackFilterCode,
    checkDuplicateShortName,
    lockAllFilters,
    unlockAllFilters,
    lockAllDataSources,
    unlockAllDataSources,
    deleteFilters,
    retrieveCyclicDependencies,
  };
});
